// The product list arrives PRODUCT_PAGE_SIZE products per request (pricerest's
// ProductView.PRODUCT_PAGE_SIZE). The API computes every page on its own, so
// once the first page brings the list's total the other pages are fetched at
// once, PRODUCT_PAGE_CONCURRENCY requests at a time, instead of one after
// another.
export const PRODUCT_PAGE_SIZE = 500;
export const PRODUCT_PAGE_CONCURRENCY = 4;

// Runs worker over items with at most `limit` of them in flight.
export function runWithConcurrency(items, limit, worker) {
    let next = 0;
    const lane = () => {
        if (next >= items.length)
            return Promise.resolve();
        const item = items[next++];
        return worker(item).then(lane);
    };
    return Promise.all(Array.from({length: Math.min(limit, items.length)}, lane));
}

const pageProducts = (page) => (page && page.products) || [];

/**
 * Fetches every page of the product list, handing each one to onPage as it
 * lands (pages finish in any order).
 *
 * fetchPage(startIndex) resolves to {products, total?}. fetchTotal(), when
 * given, asks the list's size alongside the first page, so the other pages
 * start as soon as the (fast) count answers instead of after the first page;
 * when it is missing or fails, the first page's own total is used, and without
 * that (an API from before parallel paging) the pages are walked one after
 * another until a short one. A list that grew past its total while loading is
 * walked on the same way. Products are handed over once per id: one added
 * mid-load shifts the later pages by one and would otherwise show up twice.
 * isStale() lets a newer load (another campaign, a refresh) stop this one.
 */
export async function loadProductPages({fetchPage, fetchTotal = null, onPage, onTotal = () => {},
                                        isStale = () => false,
                                        pageSize = PRODUCT_PAGE_SIZE,
                                        concurrency = PRODUCT_PAGE_CONCURRENCY}) {
    const seenIds = new Set();
    const deliver = (products) => {
        if (isStale())
            return;
        onPage(products.filter(product => {
            if (seenIds.has(product.id))
                return false;
            seenIds.add(product.id);
            return true;
        }));
    };
    const walkFrom = async (startIndex) => {
        while (!isStale()) {
            const products = pageProducts(await fetchPage(startIndex));
            deliver(products);
            if (products.length < pageSize)
                return;
            startIndex += pageSize;
        }
    };
    // Every page of a list of `total` products, the first one possibly already
    // in flight; then on past the total if the last page came back full.
    const fetchAll = async (total, firstPage) => {
        const startIndexes = [0];
        for (let startIndex = pageSize; startIndex < total; startIndex += pageSize)
            startIndexes.push(startIndex);
        let lastPageFull = false;
        await runWithConcurrency(startIndexes, concurrency, startIndex => {
            if (isStale())
                return Promise.resolve();
            const page = startIndex === 0 && firstPage ? firstPage : fetchPage(startIndex);
            return page.then(page => {
                deliver(pageProducts(page));
                if (startIndex === startIndexes[startIndexes.length - 1])
                    lastPageFull = pageProducts(page).length === pageSize;
            });
        });
        if (lastPageFull)
            await walkFrom(pageSize * startIndexes.length);
    };

    const firstPageRequest = fetchPage(0);
    if (fetchTotal) {
        // Settled here only so a failure while the count is pending is not
        // reported as unhandled; it still surfaces where the page is awaited.
        firstPageRequest.catch(() => {});
        const total = await fetchTotal().then(value => (value == null ? null : value), () => null);
        if (isStale())
            return;
        if (total !== null) {
            onTotal(total);
            return fetchAll(total, firstPageRequest);
        }
    }

    const firstPage = await firstPageRequest;
    if (isStale())
        return;
    const total = firstPage && firstPage.total != null ? firstPage.total : null;
    onTotal(total);
    if (total !== null)
        return fetchAll(total, firstPageRequest);
    deliver(pageProducts(firstPage));
    if (pageProducts(firstPage).length === pageSize)
        await walkFrom(pageSize);
}
